/**
 * M7 S4: the ledger of logs in the Career story block (spec §2.1, §2.6): logs picked up but not opened
 * (`unreadLogs`), logs opened in reading order (`readLogs`) and logs whose lead is still open (`leads`).
 *
 * - Picking a log up puts it in the ledger unread; opening it moves it to `readLogs` once. Re-reading never appends,
 *   so `readLogs.length` only grows with new reading (the claim retry rule's `readMark`, spec §2.3, relies on it).
 * - A log with a `lead` (evidence data) opens a lead when it enters the ledger; `closeLead` closes it (the player
 *   reached the site, or a claim answered it). A closed lead never reopens from the same log.
 * - Unknown log ids (not in the evidence data) are refused, so a typo can't grow the save.
 *
 * Pure and Career-in, Career-out; the evidence data is passed in (`StoryData.logsById`) so tests use their own.
 */
import type { Career } from '../career/types'
import { logger } from '../core/logger'
import type { EvidenceLog } from './schemas'
import { storyOf, withStory } from './state'

export type LogsById = ReadonlyMap<string, Pick<EvidenceLog, 'id' | 'lead'>>

/** Where a log is in the ledger. */
export type LogStatus = 'none' | 'unread' | 'read'

export function logStatus(career: Career, logId: string): LogStatus {
  const s = storyOf(career)
  if (s.readLogs.includes(logId)) return 'read'
  if (s.unreadLogs.includes(logId)) return 'unread'
  return 'none'
}

export function hasLog(career: Career, logId: string): boolean {
  return logStatus(career, logId) !== 'none'
}

export function isLeadOpen(career: Career, logId: string): boolean {
  return storyOf(career).leads.includes(logId)
}

function known(logs: LogsById, logId: string, what: string): Pick<EvidenceLog, 'id' | 'lead'> | null {
  const log = logs.get(logId)
  if (log === undefined) logger.warn(`story: ${what} of unknown log ${logId} ignored`)
  return log ?? null
}

/** Puts `logId` in the ledger, unread (a picked-up core, a Wire leak). A log already in the ledger is unchanged. */
export function addLog(career: Career, logs: LogsById, logId: string): Career {
  const log = known(logs, logId, 'pickup')
  if (log === null || hasLog(career, logId)) return career
  return withStory(career, (s) => ({
    ...s,
    unreadLogs: [...s.unreadLogs, logId],
    leads: log.lead !== undefined && !s.leads.includes(logId) ? [...s.leads, logId] : s.leads,
  }))
}

/**
 * Opens `logId`: unread (or not yet in the ledger) → read, appended to `readLogs` once. A log already read is
 * unchanged (re-reading never counts as new reading).
 */
export function readLog(career: Career, logs: LogsById, logId: string): Career {
  const status = logStatus(career, logId)
  if (status === 'read') return career
  const next = status === 'none' ? addLog(career, logs, logId) : career
  if (next === career && status === 'none') return career
  return withStory(next, (s) => ({
    ...s,
    unreadLogs: s.unreadLogs.filter((id) => id !== logId),
    readLogs: [...s.readLogs, logId],
  }))
}

/** Closes the lead `logId` opened (the same career when it is not open). */
export function closeLead(career: Career, logId: string): Career {
  if (!isLeadOpen(career, logId)) return career
  return withStory(career, (s) => ({ ...s, leads: s.leads.filter((id) => id !== logId) }))
}

/** Logs read so far (the claim retry rule's mark, spec §2.3). */
export function readCount(career: Career): number {
  return storyOf(career).readLogs.length
}
